import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";

const EventChart = ({
    series = [],
    categories = [],
    title = "Events Created",
    selectedYear,
    onYearChange
}) => {
    const [visibleSeries, setVisibleSeries] = useState([]);
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    useEffect(() => {
        // Initialize all series as visible
        if (series.length > 0) {
            setVisibleSeries(series.map(() => true));
        }
    }, [series]);

    // Custom legend click handler - prevents unselecting all series
    const handleLegendClick = (seriesName) => {
        const seriesIndex = series.findIndex(s => s.name === seriesName);
        if (seriesIndex === -1) return;

        const newVisibleSeries = [...visibleSeries];

        // Count how many are currently visible
        const currentlyVisibleCount = newVisibleSeries.filter(v => v).length;

        // If trying to hide and it's the last visible one, prevent it
        if (newVisibleSeries[seriesIndex] && currentlyVisibleCount <= 1) {
            // Don't allow hiding the last visible series
            return;
        }

        // Toggle the visibility
        newVisibleSeries[seriesIndex] = !newVisibleSeries[seriesIndex];
        setVisibleSeries(newVisibleSeries);
    };

    // Calculate max value from all series to set appropriate y-axis range
    //   const calculateMaxValue = () => {
    //     let maxValue = 0;
    //     series.forEach(s => {
    //       const seriesMax = Math.max(...(s.data || [0]));
    //       if (seriesMax > maxValue) {
    //         maxValue = seriesMax;
    //       }
    //     });

    //     // If max value is less than 5, return 5 to show 0, 1, 2, 3, 4, 5
    //     if (maxValue < 5) {
    //       return 5;
    //     }
    //     // Otherwise, round up to nearest 5
    //     return Math.ceil(maxValue / 5) * 5;
    //   };

    //   const maxYValue = calculateMaxValue();
    //   // If maxYValue is 5, show individual numbers (0, 1, 2, 3, 4, 5)
    //   // Otherwise, show intervals of 5
    //   const yAxisTickAmount = maxYValue === 5 ? 5 : Math.ceil(maxYValue / 5);

    const calculateMaxValue = () => {
        let maxValue = 0;

        // Find the highest value in the series
        series.forEach(s => {
            const seriesMax = Math.max(...(s.data || [0]));
            if (seriesMax > maxValue) {
                maxValue = seriesMax;
            }
        });

        // If max value is less than 5, return 5
        if (maxValue < 5) {
            return 5;
        }

        // Dynamic intervals based on maxValue to avoid stretching
        const possibleIntervals = [5, 10, 20, 50, 100, 200, 500, 1000];

        // Find the appropriate interval
        let interval = possibleIntervals[0]; // Default to the smallest
        for (let i = 0; i < possibleIntervals.length; i++) {
            if (maxValue <= possibleIntervals[i]) {
                interval = possibleIntervals[i];
                break;
            }
        }

        // Adjust for values that could skew the chart too much
        return Math.ceil(maxValue / interval) * interval;
    };

    const maxYValue = calculateMaxValue();

    // Dynamically set y-axis tick amount based on maxYValue
    const calculateYAxisTickAmount = (maxYValue) => {
        // Determine the number of ticks based on maxYValue
        if (maxYValue <= 50) {
            return 5;  // 5 ticks if value is small
        } else if (maxYValue <= 200) {
            return 6;  // 6 ticks for mid-range values
        } else if (maxYValue <= 1000) {
            return 8;  // 8 ticks for larger values
        } else {
            return 10;  // 10 ticks for very large values
        }
    };

    const yAxisTickAmount = calculateYAxisTickAmount(maxYValue);

    const options = {
        chart: {
            type: "line",
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        title: {
            text: title,
            align: "left",
            style: {
                fontSize: "20px",
                fontWeight: "bold",
                color: "#333"
            }
        },
        colors: ["#3CB371", "#FFA500", "#FF4136"], // green, orange, red
        stroke: {
            width: 3,
            curve: "smooth"
        },
        legend: {
            show: false
        },
        grid: {
            borderColor: "#e7e7e7",
            row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 }
        },
        xaxis: {
            categories,
            labels: {
                style: { fontSize: "13px" },
                formatter: function (value) {
                    // If value contains year (e.g., "Jan 2025"), extract only the month
                    if (typeof value === 'string' && value.includes(' ')) {
                        return value.split(' ')[0];
                    }
                    return value;
                }
            }
        },
        yaxis: {
            min: 0,
            max: maxYValue > 0 ? maxYValue : 20,
            tickAmount: yAxisTickAmount,
            forceNiceScale: false,
            decimalsInFloat: 0,
            labels: {
                style: { fontSize: "13px" },
                formatter: function (value) {
                    return Math.round(value);
                }
            }
        },
        tooltip: {
            shared: true,
            intersect: false,
            x: { format: "MMM dd" },
            y: {
                formatter: (val) => val
            }
        }
    };

    // Process series with visibility state
    const processedSeries = series.map((s, index) => ({
        ...s,
        data: visibleSeries[index] === false ? [] : s.data
    }));

    const legendColors = ["#3CB371", "#FFA500", "#FF4136"];
    const legendNames = ["Individuals", "Organizations"];

    return (
        <div style={{ background: "#fafbfc", borderRadius: 16, padding: 24 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div></div>
                <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold small">Year:</span>
                    <button
                        className={`btn btn-sm ${selectedYear === currentYear ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onYearChange(currentYear)}
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                    >
                        {currentYear}
                    </button>
                    <button
                        className={`btn btn-sm ${selectedYear === previousYear ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onYearChange(previousYear)}
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                    >
                        {previousYear}
                    </button>
                </div>
            </div>
            {/* Custom Legend */}
            <div className="d-flex justify-content-end align-items-center gap-3 mb-2">
                {legendNames.map((name, index) => (
                    <div
                        key={name}
                        onClick={() => handleLegendClick(name)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            opacity: visibleSeries[index] === false ? 0.4 : 1,
                            textDecoration: visibleSeries[index] === false ? 'line-through' : 'none'
                        }}
                    >
                        <span
                            style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: legendColors[index],
                                display: 'inline-block'
                            }}
                        />
                        <span style={{ fontSize: '12px', color: '#333' }}>{name}</span>
                    </div>
                ))}
            </div>
            <Chart
                options={options}
                series={processedSeries}
                type="line"
                height={320}
            />
        </div>
    );
};

export default EventChart;